import apiClient from '../client';
import type { Database } from '@/lib/supabase/supabase-types';

type FreelancerProfile = Database['public']['Tables']['freelancer_profiles']['Row'];
type ClientProfile = Database['public']['Tables']['client_profiles']['Row'];

export interface FreelancerProfileWithStatus extends Omit<FreelancerProfile, 'review_status' | 'submitted_for_review_at'> {
  is_enabled: boolean;
  enabled_at: string | null;
  is_featured: boolean;
  featured_at: string | null;
  review_status: 'draft' | 'submitted' | 'changes_requested' | 'approved' | 'rejected' | null;
  submitted_for_review_at: string | null;
}

export interface AdminStats {
  totalFreelancers: number;
  enabledFreelancers: number;
  featuredFreelancers: number;
  totalClients: number;
  totalSearches: number;
  pendingReviews: number;
  approvedProfiles: number;
}

export interface PlatformAnalytics {
  newContractors: number;
  newBusinesses: number;
  totalContractors: number;
  totalBusinesses: number;
  totalMessagesSent: number;
  messagesPerGenius: GeniusMessageCount[];
  businessToContractorRatio: number;
  connectionRate: number;
  totalClientsWhoMessaged: number;
  totalActiveClients: number;
  periodStart: string;
  periodEnd: string;
}

export interface GeniusMessageCount {
  geniusId: string;
  userId: string;
  displayName: string;
  messageCount: number;
  profilePictureUrl?: string;
}

export interface ReportConfig {
  recipients: string[];
  enabled: boolean;
  schedule: string;
}

export interface ScheduleConfig {
  frequency: 'daily' | 'weekly' | 'monthly';
  time: string; // HH:mm
  dayOfWeek: number; // 0-6
  dayOfMonth: number; // 1-31
  timezone: string;
}

export type ReviewAction = 'approve' | 'reject' | 'request_changes';

export interface ReviewActionParams {
  profileId: string;
  action: ReviewAction;
  comments?: string;
}

export interface FreelancerProfileUpdate {
  display_name?: string;
  headline?: string | null;
  bio?: string | null;
  skills?: string[] | null;
  languages?: string[] | null;
  hourly_rate_cents?: number | null;
  rate_range_min_cents?: number | null;
  rate_range_max_cents?: number | null;
  location?: string | null;
  website_url?: string | null;
  linkedin_url?: string | null;
  profile_picture_url?: string | null;
  email?: string | null;
  is_searchable?: boolean;
  review_status?: 'draft' | 'submitted' | 'changes_requested' | 'approved' | 'rejected';
  review_comments?: string | null;
}

export const adminService = {
  /**
   * Get all freelancer profiles (admin only)
   * @param limit - Maximum number of results (default: 100)
   */
  getAllFreelancers: (limit?: number): Promise<FreelancerProfileWithStatus[]> =>
    apiClient.get<FreelancerProfileWithStatus[]>('/admin/freelancers', {
      params: limit ? { limit } : undefined,
    }) as unknown as Promise<FreelancerProfileWithStatus[]>,

  /**
   * Get a single freelancer profile (admin only)
   * @param profileId - The freelancer profile UUID
   */
  getFreelancerProfile: (profileId: string): Promise<FreelancerProfileWithStatus> =>
    apiClient.get<FreelancerProfileWithStatus>(`/admin/freelancers/${profileId}`) as unknown as Promise<FreelancerProfileWithStatus>,

  /**
   * Update a freelancer profile (admin only)
   * @param profileId - The freelancer profile UUID
   * @param updates - Partial profile data to update
   */
  updateFreelancerProfile: (
    profileId: string,
    updates: FreelancerProfileUpdate
  ): Promise<FreelancerProfileWithStatus> =>
    apiClient.patch<FreelancerProfileWithStatus>(`/admin/freelancers/${profileId}`, updates) as unknown as Promise<FreelancerProfileWithStatus>,

  /**
   * Approve a freelancer profile (admin only)
   * @param profileId - The freelancer profile UUID
   * @param comments - Optional review comments
   */
  approveFreelancerProfile: (
    profileId: string,
    comments?: string
  ): Promise<FreelancerProfileWithStatus> =>
    apiClient.post<FreelancerProfileWithStatus>(`/admin/freelancers/${profileId}/approve`, {
      comments,
    }) as unknown as Promise<FreelancerProfileWithStatus>,

  /**
   * Reject a freelancer profile (admin only)
   * @param profileId - The freelancer profile UUID
   * @param comments - Optional review comments
   */
  rejectFreelancerProfile: (
    profileId: string,
    comments?: string
  ): Promise<FreelancerProfileWithStatus> =>
    apiClient.post<FreelancerProfileWithStatus>(`/admin/freelancers/${profileId}/reject`, {
      comments,
    }) as unknown as Promise<FreelancerProfileWithStatus>,

  /**
   * Request changes for a freelancer profile (admin only)
   * @param profileId - The freelancer profile UUID
   * @param comments - Required review comments explaining requested changes
   */
  requestChangesForFreelancerProfile: (
    profileId: string,
    comments: string
  ): Promise<FreelancerProfileWithStatus> =>
    apiClient.post<FreelancerProfileWithStatus>(
      `/admin/freelancers/${profileId}/request-changes`,
      { comments }
    ) as unknown as Promise<FreelancerProfileWithStatus>,

  /**
   * Feature a freelancer profile (admin only)
   * @param profileId - The freelancer profile UUID
   */
  featureFreelancerProfile: (profileId: string): Promise<FreelancerProfileWithStatus> =>
    apiClient.post<FreelancerProfileWithStatus>(`/admin/freelancers/${profileId}/feature`) as unknown as Promise<FreelancerProfileWithStatus>,

  /**
   * Unfeature a freelancer profile (admin only)
   * @param profileId - The freelancer profile UUID
   */
  unfeatureFreelancerProfile: (profileId: string): Promise<FreelancerProfileWithStatus> =>
    apiClient.delete<FreelancerProfileWithStatus>(`/admin/freelancers/${profileId}/feature`) as unknown as Promise<FreelancerProfileWithStatus>,

  /**
   * Enable a freelancer profile (admin only)
   * @param profileId - The freelancer profile UUID
   */
  enableFreelancerProfile: (profileId: string): Promise<FreelancerProfileWithStatus> =>
    apiClient.post<FreelancerProfileWithStatus>(`/admin/freelancers/${profileId}/enable`) as unknown as Promise<FreelancerProfileWithStatus>,

  /**
   * Disable a freelancer profile (admin only)
   * @param profileId - The freelancer profile UUID
   */
  disableFreelancerProfile: (profileId: string): Promise<FreelancerProfileWithStatus> =>
    apiClient.post<FreelancerProfileWithStatus>(`/admin/freelancers/${profileId}/disable`) as unknown as Promise<FreelancerProfileWithStatus>,

  /**
   * Delete a freelancer profile from the platform (admin only)
   */
  deleteFreelancerProfile: (profileId: string): Promise<void> =>
    apiClient.delete(`/admin/freelancers/${profileId}`) as unknown as Promise<void>,

  bulkDeleteFreelancerProfiles: (profileIds: string[]): Promise<{ deletedCount: number }> =>
    apiClient.delete('/admin/freelancers/bulk', { data: { profileIds } }) as unknown as Promise<{ deletedCount: number }>,

  deleteClientProfile: (profileId: string): Promise<void> =>
    apiClient.delete(`/admin/clients/${profileId}`) as unknown as Promise<void>,

  triggerSkoolSync: (): Promise<any> =>
    apiClient.post('/admin/skool-sync/trigger') as unknown as Promise<any>,

  /**
   * Bulk approve freelancer profiles (admin only)
   * @param comments - Optional review comments to apply to all approved profiles
   * @returns Object containing the count of approved profiles and their IDs
   */
  bulkApproveFreelancers: (comments?: string): Promise<{ approvedCount: number; profileIds: string[] }> =>
    apiClient.post<{ approvedCount: number; profileIds: string[] }>('/admin/freelancers/bulk-approve', {
      comments,
    }) as unknown as Promise<{ approvedCount: number; profileIds: string[] }>,

  /**
   * Bulk feature freelancer profiles (admin only)
   * @returns Object containing the count of featured profiles and their IDs
   */
  bulkFeatureFreelancers: (): Promise<{ featuredCount: number; profileIds: string[] }> =>
    apiClient.post<{ featuredCount: number; profileIds: string[] }>('/admin/freelancers/bulk-feature') as unknown as Promise<{ featuredCount: number; profileIds: string[] }>,

  /**
   * Get featured freelancer profiles (admin only)
   * @param limit - Maximum number of results (default: 100)
   */
  getFeaturedFreelancers: (limit?: number): Promise<FreelancerProfileWithStatus[]> =>
    apiClient.get<FreelancerProfileWithStatus[]>('/admin/freelancers/featured', {
      params: limit ? { limit } : undefined,
    }) as unknown as Promise<FreelancerProfileWithStatus[]>,

  /**
   * Get all client profiles (admin only)
   * @param limit - Maximum number of results (default: 100)
   */
  getAllClients: (limit?: number): Promise<ClientProfile[]> =>
    apiClient.get<ClientProfile[]>('/admin/clients', {
      params: limit ? { limit } : undefined,
    }) as unknown as Promise<ClientProfile[]>,

  /**
   * Get a single client profile (admin only)
   * @param profileId - The client profile UUID
   */
  getClientProfile: (profileId: string): Promise<ClientProfile> =>
    apiClient.get<ClientProfile>(`/admin/clients/${profileId}`) as unknown as Promise<ClientProfile>,

  /**
   * Get all search history (admin only)
   * @param limit - Maximum number of results (default: 100)
   */
  getSearchHistory: (limit?: number): Promise<any[]> =>
    apiClient.get<any[]>('/admin/search-history', {
      params: limit ? { limit } : undefined,
    }) as unknown as Promise<any[]>,

  /**
   * Get admin statistics (admin only)
   */
  getStats: (): Promise<AdminStats> => apiClient.get<AdminStats>('/admin/stats') as unknown as Promise<AdminStats>,

  // ============================================================
  // Admin Config Methods
  // ============================================================

  /**
   * Get all configuration values
   */
  getConfig: (): Promise<any[]> =>
    apiClient.get<any[]>('/admin/config') as unknown as Promise<any[]>,

  /**
   * Get a specific configuration value
   */
  getConfigByKey: (key: string): Promise<any> =>
    apiClient.get<any>(`/admin/config/${key}`) as unknown as Promise<any>,

  /**
   * Update a configuration value
   */
  updateConfig: (key: string, value: string | number | boolean | object): Promise<any> =>
    apiClient.put<any>('/admin/config', { key, value }) as unknown as Promise<any>,

  /**
   * Update multiple configuration values at once
   */
  updateConfigBulk: (updates: Array<{ key: string; value: string | number | boolean | object }>): Promise<any> =>
    apiClient.put<any>('/admin/config/bulk', { updates }) as unknown as Promise<any>,

  /**
   * Update both system prompts
   */
  updateSystemPrompts: (aiSystemPrompt: string, aiHelperSystemPrompt: string): Promise<any> =>
    apiClient.put<any>('/admin/config/system-prompts', {
      ai_system_prompt: aiSystemPrompt,
      ai_helper_system_prompt: aiHelperSystemPrompt,
    }) as unknown as Promise<any>,

  // ============================================================
  // Knowledge Base Methods
  // ============================================================

  /**
   * Get all knowledge base documents
   */
  getDocuments: (): Promise<any[]> =>
    apiClient.get<any[]>('/admin/knowledge-base/documents') as unknown as Promise<any[]>,

  /**
   * Get a specific document
   */
  getDocument: (id: string): Promise<any> =>
    apiClient.get<any>(`/admin/knowledge-base/documents/${id}`) as unknown as Promise<any>,

  /**
   * Get presigned URL for viewing a document
   */
  getDocumentUrl: async (id: string): Promise<string> => {
    const response = await apiClient.get<{ url: string }>(`/admin/knowledge-base/documents/${id}/url`);
    return (response as any).url || response;
  },

  /**
   * Upload a document
   */
  uploadDocument: async (file: File): Promise<any> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post<any>('/admin/knowledge-base/documents/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response as unknown as any;
  },

  /**
   * Delete a document
   */
  deleteDocument: (id: string): Promise<any> =>
    apiClient.delete<any>(`/admin/knowledge-base/documents/${id}`) as unknown as Promise<any>,

  /**
   * Trigger Bedrock sync for a document
   */
  syncDocument: (id: string): Promise<any> =>
    apiClient.post<any>(`/admin/knowledge-base/documents/${id}/sync`) as unknown as Promise<any>,

  /**
   * Get sync status
   */
  getSyncStatus: (): Promise<any> =>
    apiClient.get<any>('/admin/knowledge-base/sync-status') as unknown as Promise<any>,

  /**
   * Test Bedrock retrieval with a query
   */
  testQuery: (query: string): Promise<any> =>
    apiClient.post<any>('/admin/knowledge-base/test-query', { query }) as unknown as Promise<any>,

  // ============================================================
  // Chat Analytics Methods
  // ============================================================

  /**
   * Get conversation statistics with enhanced metrics
   */
  getConversationStats: (startDate?: string, endDate?: string): Promise<any> =>
    apiClient.get<any>('/admin/analytics/conversations', {
      params: {
        start_date: startDate,
        end_date: endDate,
      },
    }) as unknown as Promise<any>,

  /**
   * Get engagement metrics with sentiment and flags
   */
  getEngagementStats: (startDate?: string, endDate?: string): Promise<any> =>
    apiClient.get<any>('/admin/analytics/engagement', {
      params: {
        start_date: startDate,
        end_date: endDate,
      },
    }) as unknown as Promise<any>,

  /**
   * Get activity statistics with sentiment trends
   */
  getActivityStats: (startDate?: string, endDate?: string): Promise<any> =>
    apiClient.get<any>('/admin/analytics/activity', {
      params: {
        start_date: startDate,
        end_date: endDate,
      },
    }) as unknown as Promise<any>,

  /**
   * Get sentiment analytics
   */
  getSentimentAnalytics: (startDate?: string, endDate?: string): Promise<any> =>
    apiClient.get<any>('/admin/analytics/sentiment', {
      params: {
        start_date: startDate,
        end_date: endDate,
      },
    }) as unknown as Promise<any>,

  /**
   * Get flagged conversations
   */
  getFlaggedConversations: (
    startDate?: string,
    endDate?: string,
    flagReason?: 'foul_language' | 'negative_sentiment' | 'inappropriate_content' | 'other'
  ): Promise<any[]> =>
    apiClient.get<any[]>('/admin/analytics/flags', {
      params: {
        start_date: startDate,
        end_date: endDate,
        flag_reason: flagReason,
      },
    }) as unknown as Promise<any[]>,

  /**
   * Get all conversations (admin only)
   */
  getConversationTags: (): Promise<string[]> =>
    apiClient.get<string[]>('/admin/conversations/tags') as unknown as Promise<string[]>,

  getAllConversations: (filters?: {
    startDate?: string;
    endDate?: string;
    flagged?: boolean;
    role?: 'genius' | 'client';
    tags?: string[];
    sentiment?: 'positive' | 'neutral' | 'negative';
    engagement?: 'low' | 'medium' | 'high';
    limit?: number;
    offset?: number;
  }): Promise<any[]> =>
    apiClient.get<any[]>('/admin/conversations', {
      params: {
        start_date: filters?.startDate,
        end_date: filters?.endDate,
        flagged: filters?.flagged !== undefined ? String(filters.flagged) : undefined,
        role: filters?.role,
        tags: filters?.tags && filters.tags.length > 0 ? filters.tags.join(',') : undefined,
        sentiment: filters?.sentiment,
        engagement: filters?.engagement,
        limit: filters?.limit,
        offset: filters?.offset,
      },
    }) as unknown as Promise<any[]>,

  /**
   * Get conversation details with messages and analysis (admin only)
   */
  getConversationDetails: (conversationId: string): Promise<any> =>
    apiClient.get<any>(`/admin/conversations/${conversationId}`) as unknown as Promise<any>,

  // ============================================================
  // Platform Analytics Methods
  // ============================================================

  /**
   * Get platform-wide analytics metrics
   */
  getPlatformAnalytics: (startDate?: string, endDate?: string): Promise<PlatformAnalytics> =>
    apiClient.get<PlatformAnalytics>('/admin/analytics/platform', {
      params: {
        start_date: startDate,
        end_date: endDate,
      },
    }) as unknown as Promise<PlatformAnalytics>,

  /**
   * Get messages sent per genius in date range
   */
  getMessagesPerGenius: (startDate?: string, endDate?: string, limit?: number): Promise<GeniusMessageCount[]> =>
    apiClient.get<GeniusMessageCount[]>('/admin/analytics/messages-per-genius', {
      params: {
        start_date: startDate,
        end_date: endDate,
        limit,
      },
    }) as unknown as Promise<GeniusMessageCount[]>,

  // ============================================================
  // Platform Analytics Drilldown Methods
  // ============================================================

  getNewGeniusesList: (startDate?: string, endDate?: string): Promise<any[]> =>
    apiClient.get<any[]>('/admin/analytics/platform/new-geniuses', {
      params: { start_date: startDate, end_date: endDate },
    }) as unknown as Promise<any[]>,

  getNewClientsList: (startDate?: string, endDate?: string): Promise<any[]> =>
    apiClient.get<any[]>('/admin/analytics/platform/new-clients', {
      params: { start_date: startDate, end_date: endDate },
    }) as unknown as Promise<any[]>,

  getMessagesList: (startDate?: string, endDate?: string): Promise<any[]> =>
    apiClient.get<any[]>('/admin/analytics/platform/messages', {
      params: { start_date: startDate, end_date: endDate },
    }) as unknown as Promise<any[]>,

  getConnectionsList: (startDate?: string, endDate?: string): Promise<any[]> =>
    apiClient.get<any[]>('/admin/analytics/platform/connections', {
      params: { start_date: startDate, end_date: endDate },
    }) as unknown as Promise<any[]>,

  getAllGeniusesList: (): Promise<any[]> =>
    apiClient.get<any[]>('/admin/analytics/platform/all-geniuses') as unknown as Promise<any[]>,

  // ============================================================
  // Weekly Report Configuration Methods
  // ============================================================

  /**
   * Get weekly report configuration
   */
  getReportConfig: (): Promise<ReportConfig> =>
    apiClient.get<ReportConfig>('/admin/reports/config') as unknown as Promise<ReportConfig>,

  /**
   * Update weekly report configuration
   */
  updateReportConfig: (config: Partial<Pick<ReportConfig, 'recipients' | 'enabled'>>): Promise<ReportConfig> =>
    apiClient.put<ReportConfig>('/admin/reports/config', config) as unknown as Promise<ReportConfig>,

  /**
   * Manually trigger weekly report email
   */
  sendReportNow: (): Promise<{ message: string }> =>
    apiClient.post<{ message: string }>('/admin/reports/send-now') as unknown as Promise<{ message: string }>,

  /**
   * Get schedule configuration
   */
  getScheduleConfig: (): Promise<ScheduleConfig> =>
    apiClient.get<ScheduleConfig>('/admin/reports/schedule') as unknown as Promise<ScheduleConfig>,

  /**
   * Update schedule configuration
   */
  updateScheduleConfig: (config: Partial<ScheduleConfig>): Promise<ScheduleConfig> =>
    apiClient.put<ScheduleConfig>('/admin/reports/schedule', config) as unknown as Promise<ScheduleConfig>,

  // SEO Analytics (Google Search Console)
  getSEOOverview: (startDate: string, endDate: string): Promise<{ clicks: number; impressions: number; ctr: number; position: number }> =>
    apiClient.get('/admin/seo/overview', { params: { startDate, endDate } }) as any,

  getSEOTrends: (startDate: string, endDate: string): Promise<Array<{ date: string; clicks: number; impressions: number; ctr: number; position: number }>> =>
    apiClient.get('/admin/seo/trends', { params: { startDate, endDate } }) as any,

  getSEOQueries: (startDate: string, endDate: string, limit?: number): Promise<Array<{ query: string; clicks: number; impressions: number; ctr: number; position: number }>> =>
    apiClient.get('/admin/seo/queries', { params: { startDate, endDate, limit } }) as any,

  getSEOPages: (startDate: string, endDate: string, limit?: number): Promise<Array<{ page: string; clicks: number; impressions: number; ctr: number; position: number }>> =>
    apiClient.get('/admin/seo/pages', { params: { startDate, endDate, limit } }) as any,

  // Site Analytics (PostHog)
  getSiteOverview: (startDate: string, endDate: string): Promise<{ pageviews: number; uniqueVisitors: number; sessions: number }> =>
    apiClient.get('/admin/analytics/overview', { params: { startDate, endDate } }) as any,

  getSiteTrends: (startDate: string, endDate: string): Promise<Array<{ date: string; pageviews: number; visitors: number }>> =>
    apiClient.get('/admin/analytics/trends', { params: { startDate, endDate } }) as any,

  getSiteTopPages: (startDate: string, endDate: string, limit?: number): Promise<Array<{ page: string; views: number; uniqueVisitors: number }>> =>
    apiClient.get('/admin/analytics/top-pages', { params: { startDate, endDate, limit } }) as any,

  getSiteReferrers: (startDate: string, endDate: string, limit?: number): Promise<Array<{ referrer: string; visits: number }>> =>
    apiClient.get('/admin/analytics/referrers', { params: { startDate, endDate, limit } }) as any,
};

